import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { formatMoney } from '../utils/formatMoney';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { X, Truck, MapPin, Clock, Euro, Check, User, AlertCircle, ExternalLink, Loader2 } from 'lucide-react';
import efoodLogo from '../assets/logos/logo_efood.png';
import woltLogo from '../assets/logos/logo_wolt.png';
import { loadGoogleMaps, getStoreCenter } from '../utils/googleMaps';

interface Order {
  id: number;
  short_code?: string;
  customer_name?: string;
  delivery_address?: string;
  customer_latitude?: number;
  customer_longitude?: number;
  total_amount: number;
  order_timestamp: string;
  delivery_time?: string;
  order_json?: any;
  status: string;
  platform_name?: string;
  payment_type?: string;
  source?: string;
  // Laas Mile fields
  laas_mile_order_id?: string;
  laas_mile_status?: string;
  laas_mile_tracking_link?: string;
  laas_mile_driver_name?: string;
  laas_mile_driver_phone?: string;
  // Wolt Drive fields
  wolt_drive_delivery_id?: string;
  wolt_drive_status?: string;
  wolt_drive_tracking_link?: string;
  wolt_drive_courier_name?: string;
  wolt_drive_courier_phone?: string;
  // Common
  delivery_method?: string;
}

interface Driver {
  id: number;
  first_name: string;
  last_name: string;
  username: string;
  is_active: boolean;
  role_name: string;
}

interface PosTerminal {
  id: number;
  terminal_name: string;
  terminal_id: string;
  provider_name?: string;
  provider_code?: string;
  is_active: boolean;
}

// Providers that DEFINITELY don't support preload — excluded from the picker.
// Everything else (including nexi, nexi_mellon, mypos, mellon, worldline, epay)
// is shown and attempted; the backend will surface a proper error if it fails.
const PRELOAD_UNSUPPORTED_PROVIDERS = new Set(['viva', 'mypay']);

interface DispatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
  onOrderDispatched: () => void;
}

const DispatchModal: React.FC<DispatchModalProps> = ({
  isOpen,
  onClose,
  order,
  onOrderDispatched,
}) => {
  const { store } = useAuth();
  const { t } = useTranslation(['orders', 'common']);
  const [loading, setLoading] = useState(false);
  const [acceptedOrders, setAcceptedOrders] = useState<Order[]>([]); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [allOrdersForMap, setAllOrdersForMap] = useState<Order[]>([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<number>>(new Set());
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);
  const [step, setStep] = useState<'select-method' | 'select-orders' | 'select-driver'>('select-method');

  // POS preload state (self-delivery driver card flow) — πάντα ανοιχτή από 12/10/2026, χωρίς διακόπτη
  const [posTerminals, setPosTerminals] = useState<PosTerminal[]>([]);
  const [selectedPosTerminalId, setSelectedPosTerminalId] = useState<number | null>(null);
  
  // Laas Mile state
  const [laasMileEnabled, setLaasMileEnabled] = useState(false);
  const [laasMileEstimate, setLaasMileEstimate] = useState<{ fee?: number; time?: string } | null>(null);
  const [estimatingLaasMile, setEstimatingLaasMile] = useState(false);
  const [laasMileError, setLaasMileError] = useState<string | null>(null);
  const [creatingLaasMile, setCreatingLaasMile] = useState(false);
  
  // Wolt Drive state
  const [woltDriveEnabled, setWoltDriveEnabled] = useState(false);
  const [woltDriveEstimate, setWoltDriveEstimate] = useState<{ fee?: number; time?: string; promise_id?: string; valid_until?: string } | null>(null);
  const [estimatingWoltDrive, setEstimatingWoltDrive] = useState(false);
  const [woltDriveError, setWoltDriveError] = useState<string | null>(null);
  const [creatingWoltDrive, setCreatingWoltDrive] = useState(false);
  
  // Cancel state
  const [cancellingLaasMile, setCancellingLaasMile] = useState(false);
  const [cancellingWoltDrive, setCancellingWoltDrive] = useState(false);
  
  // Status refresh
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<'own' | 'laas_mile' | 'wolt_drive' | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  
  // Track if configs have been loaded
  const [configsLoaded, setConfigsLoaded] = useState({ laasMile: false, woltDrive: false });
  
  // Google Maps refs
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<number, google.maps.Marker>>(new Map());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const [mapReady, setMapReady] = useState(false);

  // Helper function to extract coordinates from any order format
  const getOrderCoordinates = (o: Order): { lat: number; lng: number } | null => {
    let lat = o.customer_latitude;
    let lng = o.customer_longitude;
    
    // Parse as float if they're strings
    if (typeof lat === 'string') lat = parseFloat(lat);
    if (typeof lng === 'string') lng = parseFloat(lng);
    
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
      // Check for internal order structure (coordinates at root of order_json)
      if (o.order_json?.latitude && o.order_json?.longitude) {
        lat = parseFloat(o.order_json.latitude);
        lng = parseFloat(o.order_json.longitude);
      }
      // Check for internal order customer structure
      else if (o.order_json?.customer?.latitude && o.order_json?.customer?.longitude) {
        lat = parseFloat(o.order_json.customer.latitude);
        lng = parseFloat(o.order_json.customer.longitude);
      }
      // Check for Wolt order structure
      else if (o.order_json?.delivery?.location?.coordinates) {
        const coords = o.order_json.delivery.location.coordinates;
        lat = parseFloat(coords.lat);
        lng = parseFloat(coords.lon);
      }
      // Check for BOX order structure
      else if (o.order_json?.address) {
        const addr = o.order_json.address;
        lat = parseFloat(addr.latitude);
        lng = parseFloat(addr.longitude);
      }
      // Check for nested orders array structure (Efood webhook format)
      else if (o.order_json?.orders?.[0]?.customer) {
        const customer = o.order_json.orders[0].customer;
        lat = parseFloat(customer.latitude);
        lng = parseFloat(customer.longitude);
      } else if (o.order_json?.customer) {
        const customer = o.order_json.customer;
        lat = parseFloat(customer.latitude);
        lng = parseFloat(customer.longitude);
      }
    }
    
    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
    return null;
  };

  // Set map center based on current order location, fallback to store coords
  const mapCenter: [number, number] = useMemo(() => {
    if (order) {
      const coords = getOrderCoordinates(order);
      if (coords) {
        return [coords.lat, coords.lng];
      }
    }
    const center = getStoreCenter(store);
    return [center.lat, center.lng];
  }, [order, store]);

  // Initialize Google Maps when step changes to select-orders
  useEffect(() => {
    if (step !== 'select-orders') return;
    
    const initMap = async () => {
      await loadGoogleMaps(store?.google_maps_api_key);
      // Small delay to ensure the container is rendered
      await new Promise(r => setTimeout(r, 100));
      
      if (!mapContainerRef.current || !window.google) return;
      
      mapInstanceRef.current = new google.maps.Map(mapContainerRef.current, {
        center: { lat: mapCenter[0], lng: mapCenter[1] },
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }
        ]
      });
      
      infoWindowRef.current = new google.maps.InfoWindow();
      setMapReady(true);
    };
    
    initMap();
    
    return () => {
      // Cleanup markers on unmount
      markersRef.current.forEach(m => m.setMap(null));
      markersRef.current.clear();
      mapInstanceRef.current = null;
      setMapReady(false);
    };
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync markers when orders or selection changes
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return;
    const map = mapInstanceRef.current;
    const iw = infoWindowRef.current;
    
    // Track which order IDs are still present
    const currentOrderIds = new Set<number>();
    
    allOrdersForMap.forEach((mapOrder) => {
      const coords = getOrderCoordinates(mapOrder);
      if (!coords) return;
      
      currentOrderIds.add(mapOrder.id);
      const isCurrentOrder = order && mapOrder.id === order.id;
      const isSelected = selectedOrderIds.has(mapOrder.id);
      
      // Marker pin color
      const pinColor = isCurrentOrder ? '#EF4444' : isSelected ? '#22C55E' : '#3B82F6';
      const pinIcon = {
        path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
        scale: 7,
        fillColor: pinColor,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        anchor: new google.maps.Point(0, 0),
      };
      
      const existing = markersRef.current.get(mapOrder.id);
      if (existing) {
        existing.setPosition({ lat: coords.lat, lng: coords.lng });
        existing.setIcon(pinIcon);
      } else {
        const marker = new google.maps.Marker({
          position: { lat: coords.lat, lng: coords.lng },
          map,
          icon: pinIcon,
          title: getCustomerName(mapOrder),
        });
        
        marker.addListener('click', () => {
          toggleOrderSelection(mapOrder.id);
          if (iw) {
            iw.setContent(`
              <div style="padding:8px;min-width:180px;">
                <p style="font-weight:bold;font-size:14px;margin:0 0 4px">${getCustomerName(mapOrder)}</p>
                <p style="color:#666;font-size:12px;margin:0 0 4px">${getAddress(mapOrder)}</p>
                <p style="font-weight:600;font-size:14px;margin:0">${formatMoney((typeof mapOrder.total_amount === 'number' ? mapOrder.total_amount : parseFloat(mapOrder.total_amount || '0')))}</p>
              </div>
            `);
            iw.open(map, marker);
          }
        });
        
        markersRef.current.set(mapOrder.id, marker);
      }
    });
    
    // Remove markers for orders no longer in list
    markersRef.current.forEach((marker, id) => {
      if (!currentOrderIds.has(id)) {
        marker.setMap(null);
        markersRef.current.delete(id);
      }
    });
    
    // Fit bounds if multiple markers
    if (markersRef.current.size > 1) {
      const bounds = new google.maps.LatLngBounds();
      markersRef.current.forEach(m => {
        const pos = m.getPosition();
        if (pos) bounds.extend(pos);
      });
      map.fitBounds(bounds);
    } else if (markersRef.current.size === 1) {
      const pos = Array.from(markersRef.current.values())[0].getPosition();
      if (pos) {
        map.setCenter(pos);
        map.setZoom(14);
      }
    }
  }, [mapReady, allOrdersForMap, selectedOrderIds, order]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) {
      setStep('select-method');
      setSelectedDriverId(null);
      setSelectedDeliveryMethod(null);
      setLaasMileError(null);
      setLaasMileEstimate(null);
      setWoltDriveError(null);
      setWoltDriveEstimate(null);
      setConfigsLoaded({ laasMile: false, woltDrive: false });
      setMapReady(false);
      setSelectedPosTerminalId(null);
      fetchAcceptedOrders();
      fetchDrivers();
      fetchLaasMileConfig();
      fetchWoltDriveConfig();
      fetchPreloadTerminals();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Skip method selection if only own delivery is available
  useEffect(() => {
    if (isOpen && configsLoaded.laasMile && configsLoaded.woltDrive && step === 'select-method') {
      if (!laasMileEnabled && !woltDriveEnabled) {
        // Only own delivery available - skip to driver selection
        setSelectedDeliveryMethod('own');
        setStep('select-orders');
      }
    }
  }, [isOpen, configsLoaded, laasMileEnabled, woltDriveEnabled, step]);

  // Fetch Laas Mile configuration
  const fetchLaasMileConfig = async () => {
    if (!store?.id) return;
    
    try {
      const response = await api.get(`/laas-mile/${store.id}/config`);
      setLaasMileEnabled(response.data.laas_mile_enabled || false);
      
      // If enabled and order exists, get estimate
      if (response.data.laas_mile_enabled && order) {
        fetchLaasMileEstimate();
      }
    } catch (error) {
      console.error('Error fetching Laas Mile config:', error);
      setLaasMileEnabled(false);
    } finally {
      setConfigsLoaded(prev => ({ ...prev, laasMile: true }));
    }
  };

  // Fetch Laas Mile delivery estimate
  const fetchLaasMileEstimate = async () => {
    if (!store?.id || !order) return;
    
    setEstimatingLaasMile(true);
    try {
      const coords = getOrderCoordinates(order);
      if (!coords) {
        return;
      }
      
      const address = getAddress(order);
      
      const [feeResponse, timeResponse] = await Promise.all([
        api.post(`/laas-mile/${store.id}/estimate-fee`, {
          customer_address: address,
          customer_latitude: coords.lat,
          customer_longitude: coords.lng,
          total_amount: order.total_amount || 0,
          payment_type: order.payment_type || 'cash'
        }),
        api.post(`/laas-mile/${store.id}/estimate-time`, {
          customer_address: address,
          customer_latitude: coords.lat,
          customer_longitude: coords.lng,
          total_amount: order.total_amount || 0,
          payment_type: order.payment_type || 'cash'
        })
      ]);
      
      setLaasMileEstimate({
        fee: feeResponse.data.estimate_delivery_fee,
        time: timeResponse.data.estimate_delivery_time
      });
    } catch (error) {
      console.error('Error fetching Laas Mile estimate:', error);
    } finally {
      setEstimatingLaasMile(false);
    }
  };

  // Check if order can use Laas Mile (only cash for internal orders, not for efood orders)
  const canUseLaasMile = (): { allowed: boolean; reason?: string } => {
    if (!order) return { allowed: false, reason: 'No order selected' };
    
    // If already has Laas Mile delivery
    if (order.laas_mile_order_id) {
      return { allowed: false, reason: 'Order already has Laas Mile delivery' };
    }
    
    // Cannot use Laas Mile for efood orders (same platform)
    if (order.platform_name === 'efood' || order.source === 'efood') {
      return { allowed: false, reason: t('orders:dispatch.laasMileNotAllowed') };
    }
    
    // Platform orders (non-efood) can always use Laas Mile
    if (order.platform_name !== 'internal' && order.source !== 'internal') {
      return { allowed: true };
    }
    
    // Internal orders: only cash
    if (order.payment_type === 'cash') {
      return { allowed: true };
    }
    
    return {
      allowed: false,
      reason: t('orders:dispatch.laasMileCashOnly')
    };
  };

  // Create Laas Mile delivery
  const handleCreateLaasMileDelivery = async () => {
    if (!store?.id || !order) return;
    
    setCreatingLaasMile(true);
    setLaasMileError(null);
    
    try {
      await api.post(`/laas-mile/${store.id}/create/${order.id}`);
      onOrderDispatched();
      onClose();
    } catch (error: any) {
      console.error('Error creating Laas Mile delivery:', error);
      setLaasMileError(error.response?.data?.error || t('orders:dispatch.laasMileCreateError'));
    } finally {
      setCreatingLaasMile(false);
    }
  };

  // Fetch Wolt Drive configuration
  const fetchWoltDriveConfig = async () => {
    if (!store?.id) return;
    
    try {
      const response = await api.get(`/wolt-drive/${store.id}/config`);
      setWoltDriveEnabled(response.data.wolt_drive_enabled || false);
      
      // If enabled and order exists, get estimate
      if (response.data.wolt_drive_enabled && order) {
        fetchWoltDriveEstimate();
      }
    } catch (error) {
      console.error('Error fetching Wolt Drive config:', error);
      setWoltDriveEnabled(false);
    } finally {
      setConfigsLoaded(prev => ({ ...prev, woltDrive: true }));
    }
  };

  // Fetch Wolt Drive delivery estimate
  const fetchWoltDriveEstimate = async () => {
    if (!store?.id || !order) return;
    
    setEstimatingWoltDrive(true);
    try {
      const coords = getOrderCoordinates(order);
      if (!coords) {
        return;
      }
      
      const address = getAddress(order);
      // Parse address for Wolt Drive format
      const addressParts = address.split(',');
      const street = addressParts[0]?.trim() || address;
      const city = addressParts[1]?.trim() || 'Αθήνα';
      
      const response = await api.post(`/wolt-drive/${store.id}/estimate`, {
        street,
        city,
        lat: coords.lat,
        lon: coords.lng
      });
      
      setWoltDriveEstimate({
        fee: response.data.price?.amount,
        time: response.data.estimated_delivery_time,
        promise_id: response.data.promise_id,
        valid_until: response.data.valid_until
      });
    } catch (error) {
      console.error('Error fetching Wolt Drive estimate:', error);
    } finally {
      setEstimatingWoltDrive(false);
    }
  };

  // Check if order can use Wolt Drive (only cash for internal orders, not for wolt orders)
  const canUseWoltDrive = (): { allowed: boolean; reason?: string } => {
    if (!order) return { allowed: false, reason: 'No order selected' };
    
    // If already has Wolt Drive delivery
    if (order.wolt_drive_delivery_id) {
      return { allowed: false, reason: 'Order already has Wolt Drive delivery' };
    }
    
    // Cannot use Wolt Drive for Wolt orders (same platform)
    if (order.platform_name === 'wolt' || order.source === 'wolt') {
      return { allowed: false, reason: t('orders:dispatch.woltDriveNotAllowed') };
    }
    
    // Platform orders (non-wolt) can always use Wolt Drive
    if (order.platform_name !== 'internal' && order.source !== 'internal') {
      return { allowed: true };
    }
    
    // Internal orders: only cash
    if (order.payment_type === 'cash') {
      return { allowed: true };
    }
    
    return {
      allowed: false,
      reason: t('orders:dispatch.woltDriveCashOnly')
    };
  };

  // Create Wolt Drive delivery
  const handleCreateWoltDriveDelivery = async () => {
    if (!store?.id || !order) return;
    
    setCreatingWoltDrive(true);
    setWoltDriveError(null);
    
    try {
      const coords = getOrderCoordinates(order);
      const address = getAddress(order);
      const addressParts = address.split(',');
      const street = addressParts[0]?.trim() || address;
      const city = addressParts[1]?.trim() || 'Αθήνα';
      
      await api.post(`/wolt-drive/${store.id}/create/${order.id}`, {
        promise_id: woltDriveEstimate?.promise_id,
        dropoff: woltDriveEstimate?.promise_id ? undefined : {
          street,
          city,
          lat: coords?.lat,
          lon: coords?.lng
        }
      });
      onOrderDispatched();
      onClose();
    } catch (error: any) {
      console.error('Error creating Wolt Drive delivery:', error);
      // Check for expired promise
      if (error.response?.data?.error?.includes('expired') || error.response?.data?.error?.includes('invalid')) {
        setWoltDriveError(t('orders:dispatch.woltDriveOfferExpired'));
        // Refresh estimate
        fetchWoltDriveEstimate();
      } else {
        setWoltDriveError(error.response?.data?.error || t('orders:dispatch.woltDriveCreateError'));
      }
    } finally {
      setCreatingWoltDrive(false);
    }
  };

  // Cancel Laas Mile delivery
  const handleCancelLaasMile = async () => {
    if (!store?.id || !order?.laas_mile_order_id) return;
    
    if (!window.confirm(t('orders:dispatch.cancelLaasMileConfirm'))) {
      return;
    }
    
    setCancellingLaasMile(true);
    try {
      await api.post(`/laas-mile/${store.id}/cancel/${order.id}`, {
        reason: 'Cancelled by store'
      });
      onOrderDispatched(); // Refresh order data
    } catch (error: any) {
      console.error('Error cancelling Laas Mile:', error);
      setLaasMileError(error.response?.data?.error || t('orders:dispatch.cancelDeliveryError'));
    } finally {
      setCancellingLaasMile(false);
    }
  };

  // Cancel Wolt Drive delivery
  const handleCancelWoltDrive = async () => {
    if (!store?.id || !order?.wolt_drive_delivery_id) return;
    
    if (!window.confirm(t('orders:dispatch.cancelWoltDriveConfirm'))) {
      return;
    }
    
    setCancellingWoltDrive(true);
    try {
      await api.post(`/wolt-drive/${store.id}/cancel/${order.id}`);
      onOrderDispatched(); // Refresh order data
    } catch (error: any) {
      console.error('Error cancelling Wolt Drive:', error);
      setWoltDriveError(error.response?.data?.error || t('orders:dispatch.cancelDeliveryError'));
    } finally {
      setCancellingWoltDrive(false);
    }
  };

  // Refresh delivery status
  const handleRefreshStatus = async () => {
    if (!store?.id || !order) return;
    
    setRefreshingStatus(true);
    try {
      if (order.laas_mile_order_id) {
        await api.get(`/laas-mile/${store.id}/status/${order.id}`);
      }
      if (order.wolt_drive_delivery_id) {
        await api.get(`/wolt-drive/${store.id}/status/${order.id}`);
      }
      onOrderDispatched(); // Refresh order data
    } catch (error) {
      console.error('Error refreshing status:', error);
    } finally {
      setRefreshingStatus(false);
    }
  };

  // Check if Wolt Drive promise is expired
  const isWoltDrivePromiseExpired = (): boolean => {
    if (!woltDriveEstimate?.valid_until) return false;
    return new Date(woltDriveEstimate.valid_until) < new Date();
  };

  // Get cancellable status for Laas Mile
  const canCancelLaasMile = (): boolean => {
    if (!order?.laas_mile_status) return false;
    const nonCancellable = ['PICKED_UP', 'COURIER_LEFT_VENDOR', 'NEAR_CUSTOMER', 'DELIVERED', 'CANCELLED'];
    return !nonCancellable.includes(order.laas_mile_status);
  };

  // Get cancellable status for Wolt Drive
  const canCancelWoltDrive = (): boolean => {
    if (!order?.wolt_drive_status) return false;
    const nonCancellable = ['PICKED_UP', 'DELIVERED', 'CANCELLED', 'FAILED'];
    return !nonCancellable.includes(order.wolt_drive_status);
  };

  const fetchDrivers = async () => {
    if (!store?.id) return;

    try {
      // Use the drivers endpoint which returns only active drivers
      const response = await api.get(`/orders/${store.id}/drivers`);
      setDrivers(response.data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  // The list of POS terminals for this store. The terminal picker is shown when at least one
  // of the selected orders is card (preload is always on — no pos_preload switch since 12/10/2026).
  const fetchPreloadTerminals = async () => {
    if (!store?.id) return;
    try {
      const termRes = await api.get(`/stores/${store.id}/pos/terminals`);
      const terminals: PosTerminal[] = Array.isArray(termRes.data) ? termRes.data : [];
      // Exclude only the providers that definitely can't preload (viva, mypay).
      // Everything else is shown and attempted.
      setPosTerminals(terminals.filter(t =>
        t.is_active && !(t.provider_code && PRELOAD_UNSUPPORTED_PROVIDERS.has(t.provider_code.toLowerCase()))
      ));
    } catch (error) {
      console.error('Error fetching POS terminals:', error);
      setPosTerminals([]);
    }
  };

  const fetchAcceptedOrders = async () => {
    if (!store?.id) return;
    
    try {
      // Create a set of self-delivery platforms - always include 'internal'
      const selfDeliveryPlatforms = new Set<string>(['internal']);
      
      try {
        // Try to get platform credentials to check self-delivery status
        const credentialsResponse = await api.get(`/orders/${store.id}/platform-credentials`);
        const platformCredentials = credentialsResponse.data;
        
        // Add platforms with self-delivery to the set
        platformCredentials.forEach((cred: any) => {
          if (cred.is_self_delivery === true || cred.is_self_delivery === 1 || cred.is_self_delivery === '1') {
            selfDeliveryPlatforms.add(cred.platform_name);
          }
        });
        
      } catch (credError) {
        console.warn('Could not fetch platform credentials:', credError);
        // Continue with just 'internal' as self-delivery
      }
      
      // Get all pending and accepted delivery orders for self-delivery platforms
      // Internal orders can be pending, platform orders need to be accepted
      const [pendingResponse, acceptedResponse] = await Promise.all([
        api.get(`/stores/${store.id}/orders?status=pending`),
        api.get(`/stores/${store.id}/orders?status=accepted`)
      ]);
      
      const pendingOrders = Array.isArray(pendingResponse.data) 
        ? pendingResponse.data 
        : (pendingResponse.data?.orders || []);
      const acceptedOrders = Array.isArray(acceptedResponse.data) 
        ? acceptedResponse.data 
        : (acceptedResponse.data?.orders || []);
      
      // Combine and filter orders
      const allOrders = [...pendingOrders, ...acceptedOrders];
      
      // Filter to only self-delivery orders
      const selfDeliveryOrders = allOrders.filter((o: Order) => {
        // Exclude current order from the list (it will be added separately)
        if (order && o.id === order.id) return false;
        
        const platformName = o.platform_name || '';
        
        // Include if platform is self-delivery
        if (selfDeliveryPlatforms.has(platformName)) {
          // For internal orders, only include delivery type
          if (platformName === 'internal') {
            const orderType = o.order_json?.type || o.order_json?.orderType || '';
            return orderType === 'delivery';
          }
          return true;
        }
        
        return false;
      });
      
      setAcceptedOrders(selfDeliveryOrders);
      
      // For map display - always include current order + other self-delivery orders
      let ordersForMap = selfDeliveryOrders;
      if (order) {
        ordersForMap = [order, ...selfDeliveryOrders];
        setSelectedOrderIds(new Set([order.id]));
      }
      
      setAllOrdersForMap(ordersForMap);
      
    } catch (error) {
      console.error('Error fetching orders for dispatch:', error);
    }
  };

  const handleDispatch = async () => {
    if (!store?.id) return;
    
    if (!selectedDriverId) {
      alert(t('orders:dispatch.selectDriverAlert'));
      return;
    }
    
    setLoading(true);
    try {
      const orderIds = Array.from(selectedOrderIds);
      
      // Assign orders to driver using the delivery endpoint.
      // pos_terminal_id is optional — only passed when the cashier picked a terminal
      // for a card order. Backend ignores it when null.
      await Promise.all(
        orderIds.map(orderId =>
          api.post(`/delivery/${store.id}/orders/${orderId}/assign`, {
            driver_id: selectedDriverId,
            pos_terminal_id: selectedPosTerminalId || null
          })
        )
      );
      
      onOrderDispatched();
      onClose();
    } catch (error) {
      console.error('Error dispatching orders:', error);
      alert(t('orders:dispatch.dispatchError'));
    } finally {
      setLoading(false);
    }
  };

  const toggleOrderSelection = (orderId: number) => {
    const newSelection = new Set(selectedOrderIds);
    if (newSelection.has(orderId)) {
      // Don't allow deselecting the current order
      if (order && orderId === order.id) return;
      newSelection.delete(orderId);
    } else {
      newSelection.add(orderId);
    }
    setSelectedOrderIds(newSelection);
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) {
      return t('orders:dispatch.minutesAgo', { count: diffMins });
    } else {
      const hours = Math.floor(diffMins / 60);
      return t('orders:dispatch.hoursMinutesAgo', { hours, minutes: diffMins % 60 });
    }
  };

  const formatDeliveryTime = (minutes: string | number): string => { // eslint-disable-line @typescript-eslint/no-unused-vars
    const mins = typeof minutes === 'string' ? parseInt(minutes) || 0 : minutes;
    if (!mins) return t('orders:dispatch.immediately');
    
    if (mins < 60) {
      return `${mins} min`;
    } else {
      const hours = Math.floor(mins / 60);
      const remainingMins = mins % 60;
      return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
    }
  };

  if (!isOpen) return null;

  // Helper to get customer name from order
  const getCustomerName = (o: Order): string => {
    if (o.customer_name) return o.customer_name;
    if (o.order_json?.consumer_name) return o.order_json.consumer_name;
    if (o.order_json?.user) {
      const user = o.order_json.user;
      return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown';
    }
    if (o.order_json?.orders?.[0]?.customer) {
      const customer = o.order_json.orders[0].customer;
      return `${customer.name || ''} ${customer.surname || ''}`.trim();
    }
    if (o.order_json?.customer) {
      return `${o.order_json.customer.name || ''} ${o.order_json.customer.surname || ''}`.trim();
    }
    return t('orders:unknownCustomer');
  };

  // Helper to get address from order
  const getAddress = (o: Order): string => {
    if (o.delivery_address) return o.delivery_address;
    if (o.order_json?.delivery?.location) {
      const loc = o.order_json.delivery.location;
      return [loc.street_address, loc.apartment && `Apt: ${loc.apartment}`, loc.city].filter(Boolean).join(', ');
    }
    if (o.order_json?.address) {
      const addr = o.order_json.address;
      return [addr.street, addr.streetNo, addr.city].filter(Boolean).join(', ');
    }
    if (o.order_json?.orders?.[0]?.customer) {
      const c = o.order_json.orders[0].customer;
      return c.address || `${c.street || ''} ${c.street_number || ''}, ${c.city || ''}`.trim();
    }
    if (o.order_json?.customer) {
      const c = o.order_json.customer;
      return c.address || `${c.street || ''} ${c.street_number || ''}, ${c.city || ''}`.trim();
    }
    return t('orders:noAddress');
  };

  // Calculate total for selected orders
  const selectedTotal = Array.from(selectedOrderIds).reduce((sum, id) => {
    const o = allOrdersForMap.find(ord => ord.id === id);
    if (o) {
      return sum + (typeof o.total_amount === 'number' ? o.total_amount : parseFloat(o.total_amount || '0'));
    }
    return sum;
  }, 0);

  // True when at least one of the currently-selected orders will be paid by
  // card (so the driver needs a preloaded POS terminal on arrival).
  const hasCardOrder = Array.from(selectedOrderIds).some(id => {
    const o = allOrdersForMap.find(ord => ord.id === id);
    const pm = (o?.payment_type || o?.order_json?.payment_method || 'cash').toLowerCase();
    return pm === 'card' || pm === 'pos';
  });
  const showTerminalPicker = hasCardOrder && posTerminals.length > 0;

  return (
    <div className="cashier-overlay fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="cashier-dialog bg-surface-1 rounded-xl shadow-2xl border border-line w-[98%] max-w-7xl h-[95vh] flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="cashier-dialog-header bg-surface-0 text-t1 px-6 py-4 flex items-center justify-between border-b border-line">
          <div className="flex items-center gap-4">
            <Truck className="h-7 w-7 text-blue-600 dark:text-blue-400" />
            <div>
              <h2 className="text-xl font-bold">{t('orders:dispatch.title')}</h2>
              <p className="text-t3 text-sm">
                {step === 'select-method' ? t('orders:dispatch.stepSelectMethod') :
                 step === 'select-orders' ? t('orders:dispatch.stepSelectOrders') :
                 t('orders:dispatch.stepSelectDriver')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-2 rounded-lg transition-colors text-t3 hover:text-t1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Step 1: Select Delivery Method */}
        {step === 'select-method' ? (
          <div className="flex-1 p-8 overflow-y-auto">
            <div className="max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-t1 mb-2">{t('orders:dispatch.deliveryMethodTitle')}</h3>
              <p className="text-t3 mb-8">
                {t('orders:dispatch.deliveryMethodSubtitle', { id: order?.id })}
              </p>
              
              {/* Active Delivery Status Section */}
              {(order?.laas_mile_order_id || order?.wolt_drive_delivery_id) && (
                <div className="mb-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-t1">{t('orders:dispatch.activeDeliveries')}</h4>
                    <button
                      onClick={handleRefreshStatus}
                      disabled={refreshingStatus}
                      className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-50"
                    >
                      {refreshingStatus ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      )}
                      {t('orders:dispatch.refresh')}
                    </button>
                  </div>
                  
                  {/* Laas Mile Status */}
                  {order?.laas_mile_order_id && (
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <img src={efoodLogo} alt="Laas Mile" className="w-10 h-10 object-contain" />
                          <div>
                            <h4 className="font-semibold text-red-700 dark:text-red-300">Laas Mile</h4>
                            <p className="text-xs text-red-600 dark:text-red-400">ID: {order.laas_mile_order_id}</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          order.laas_mile_status === 'DELIVERED' ? 'bg-green-500/20 text-green-700 dark:text-green-300' :
                          order.laas_mile_status === 'CANCELLED' ? 'bg-surface-3 text-t2' :
                          order.laas_mile_status === 'PICKED_UP' ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300' :
                          'bg-yellow-500/20 text-yellow-800 dark:text-yellow-300'
                        }`}>
                          {order.laas_mile_status}
                        </span>
                      </div>
                      
                      {order.laas_mile_driver_name && (
                        <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300 mb-3">
                          <User className="w-4 h-4" />
                          <span>{order.laas_mile_driver_name}</span>
                          {order.laas_mile_driver_phone && (
                            <a href={`tel:${order.laas_mile_driver_phone}`} className="text-blue-600 hover:underline">
                              {order.laas_mile_driver_phone}
                            </a>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-3">
                        {order.laas_mile_tracking_link && (
                          <a 
                            href={order.laas_mile_tracking_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                            {t('orders:dispatch.tracking')}
                          </a>
                        )}
                        {canCancelLaasMile() && (
                          <button
                            onClick={handleCancelLaasMile}
                            disabled={cancellingLaasMile}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-500/15 text-red-700 dark:text-red-300 rounded-lg text-sm hover:bg-red-500/25 disabled:opacity-50"
                          >
                            {cancellingLaasMile ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                            {t('orders:dispatch.cancelDelivery')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Wolt Drive Status */}
                  {order?.wolt_drive_delivery_id && (
                    <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <img src={woltLogo} alt="Wolt Drive" className="w-10 h-10 object-contain" />
                          <div>
                            <h4 className="font-semibold text-cyan-700 dark:text-cyan-300">Wolt Drive</h4>
                            <p className="text-xs text-cyan-600 dark:text-cyan-400">ID: {order.wolt_drive_delivery_id}</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          order.wolt_drive_status === 'DELIVERED' ? 'bg-green-500/20 text-green-700 dark:text-green-300' :
                          order.wolt_drive_status === 'CANCELLED' || order.wolt_drive_status === 'FAILED' ? 'bg-surface-3 text-t2' :
                          order.wolt_drive_status === 'PICKED_UP' ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300' :
                          'bg-yellow-500/20 text-yellow-800 dark:text-yellow-300'
                        }`}>
                          {order.wolt_drive_status}
                        </span>
                      </div>
                      
                      {order.wolt_drive_courier_name && (
                        <div className="flex items-center gap-2 text-sm text-cyan-700 dark:text-cyan-300 mb-3">
                          <User className="w-4 h-4" />
                          <span>{order.wolt_drive_courier_name}</span>
                          {order.wolt_drive_courier_phone && (
                            <a href={`tel:${order.wolt_drive_courier_phone}`} className="text-blue-600 hover:underline">
                              {order.wolt_drive_courier_phone}
                            </a>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-3">
                        {order.wolt_drive_tracking_link && (
                          <a 
                            href={order.wolt_drive_tracking_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                            {t('orders:dispatch.tracking')}
                          </a>
                        )}
                        {canCancelWoltDrive() && (
                          <button
                            onClick={handleCancelWoltDrive}
                            disabled={cancellingWoltDrive}
                            className="flex items-center gap-1 px-3 py-1.5 bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 rounded-lg text-sm hover:bg-cyan-500/25 disabled:opacity-50"
                          >
                            {cancellingWoltDrive ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                            {t('orders:dispatch.cancelDelivery')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Warning for POS payments */}
              {!canUseLaasMile().allowed && canUseLaasMile().reason && (
                <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-amber-700 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-800 dark:text-amber-300">{canUseLaasMile().reason}</p>
                </div>
              )}
              
              <div className="space-y-4">
                {/* Own Delivery Option */}
                <button
                  onClick={() => {
                    setSelectedDeliveryMethod('own');
                    setStep('select-orders');
                  }}
                  className="w-full p-6 rounded-xl border-2 border-line-strong hover:border-blue-500 hover:bg-blue-500/10 transition-all text-left group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-500/20 rounded-full flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                      <Truck className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xl font-bold text-t1">{t('orders:dispatch.ownDriverTitle')}</p>
                      <p className="text-t3">{t('orders:dispatch.ownDriverDesc')}</p>
                    </div>
                    <div className="text-t4 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      →
                    </div>
                  </div>
                </button>
                
                {/* Laas Mile Option */}
                {laasMileEnabled && (
                  <button
                    onClick={() => canUseLaasMile().allowed && handleCreateLaasMileDelivery()}
                    disabled={!canUseLaasMile().allowed || creatingLaasMile || !!order?.laas_mile_order_id}
                    className={`w-full p-6 rounded-xl border-2 transition-all text-left ${
                      !canUseLaasMile().allowed || !!order?.laas_mile_order_id
                        ? 'border-line bg-surface-2 dark:bg-surface-2/30 opacity-50 cursor-not-allowed' 
                        : 'border-line-strong hover:border-red-500 hover:bg-red-500/10 group'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <img src={efoodLogo} alt="Laas Mile" className="w-14 h-14 object-contain" />
                      <div className="flex-1">
                        <p className="text-xl font-bold text-t1">Efood Laas Mile</p>
                        <p className="text-t3">{t('orders:dispatch.efoodDesc')}</p>

                        {/* Estimate info */}
                        {estimatingLaasMile ? (
                          <div className="flex items-center gap-2 mt-2 text-t4">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">{t('orders:dispatch.estimating')}</span>
                          </div>
                        ) : laasMileEstimate && canUseLaasMile().allowed && (
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="flex items-center gap-1 text-t2">
                              <Euro className="w-4 h-4" />
                              {laasMileEstimate.fee != null ? formatMoney(laasMileEstimate.fee) : '€-'}
                            </span>
                            {laasMileEstimate.time && (
                              <span className="flex items-center gap-1 text-t2">
                                <Clock className="w-4 h-4" />
                                ~{new Date(laasMileEstimate.time).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      {creatingLaasMile ? (
                        <Loader2 className="w-6 h-6 text-red-500 animate-spin" />
                      ) : (
                        <div className="text-t4 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                          →
                        </div>
                      )}
                    </div>
                  </button>
                )}
                
                {/* Wolt Drive Option */}
                {woltDriveEnabled && (
                  <button
                    onClick={handleCreateWoltDriveDelivery}
                    disabled={!canUseWoltDrive().allowed || !!order?.wolt_drive_delivery_id || creatingWoltDrive || isWoltDrivePromiseExpired()}
                    className={`w-full p-6 rounded-xl border-2 transition-all text-left ${
                      !canUseWoltDrive().allowed || !!order?.wolt_drive_delivery_id || isWoltDrivePromiseExpired()
                        ? 'border-line bg-surface-2 dark:bg-surface-2/30 opacity-50 cursor-not-allowed' 
                        : 'border-line-strong hover:border-cyan-500 hover:bg-cyan-500/10 group'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <img src={woltLogo} alt="Wolt Drive" className="w-14 h-14 object-contain" />
                      <div className="flex-1">
                        <p className="text-xl font-bold text-t1">Wolt Drive</p>
                        <p className="text-t3">{t('orders:dispatch.woltDesc')}</p>

                        {/* Estimate info */}
                        {estimatingWoltDrive ? (
                          <div className="flex items-center gap-2 mt-2 text-t4">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">{t('orders:dispatch.estimating')}</span>
                          </div>
                        ) : woltDriveEstimate && canUseWoltDrive().allowed && (
                          <div className="mt-2">
                            <div className="flex items-center gap-4 text-sm">
                              <span className="flex items-center gap-1 text-t2">
                                <Euro className="w-4 h-4" />
                                {woltDriveEstimate.fee != null ? formatMoney(woltDriveEstimate.fee) : '€-'}
                              </span>
                              {woltDriveEstimate.time && (
                                <span className="flex items-center gap-1 text-t2">
                                  <Clock className="w-4 h-4" />
                                  ~{woltDriveEstimate.time}
                                </span>
                              )}
                            </div>
                            {/* Promise expiration warning */}
                            {isWoltDrivePromiseExpired() && (
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-amber-700 dark:text-amber-400">{t('orders:dispatch.offerExpired')}</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); fetchWoltDriveEstimate(); }}
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                  {t('orders:dispatch.refresh')}
                                </button>
                              </div>
                            )}
                            {woltDriveEstimate.valid_until && !isWoltDrivePromiseExpired() && (
                              <p className="text-xs text-t3 dark:text-gray-400 mt-1">
                                {t('orders:dispatch.validUntil', { time: new Date(woltDriveEstimate.valid_until).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) })}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      {creatingWoltDrive ? (
                        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                      ) : (
                        <div className="text-t4 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                          →
                        </div>
                      )}
                    </div>
                  </button>
                )}
              </div>
              
              {/* Error messages */}
              {laasMileError && (
                <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 dark:text-red-300">{laasMileError}</p>
                </div>
              )}
              {woltDriveError && (
                <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 dark:text-red-300">{woltDriveError}</p>
                </div>
              )}
            </div>
          </div>
        ) : step === 'select-orders' ? (
          <>
            {/* Main Content - Orders Selection */}
            <div className="flex-1 flex overflow-hidden">
              {/* Map Section */}
              <div className="flex-1 relative">
                <div ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />
              </div>

              {/* Orders List - Touch Friendly */}
              <div className="w-[400px] border-l border-line bg-surface-1 flex flex-col">
                <div className="p-4 bg-surface-0 border-b border-line">
                  <h3 className="font-bold text-lg text-t1">
                    {t('orders:dispatch.pendingDeliveries', { count: allOrdersForMap.length })}
                  </h3>
                  <p className="text-sm text-t3">{t('orders:dispatch.tapToSelect')}</p>
                </div>
                
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  {allOrdersForMap.map((o) => {
                    const isSelected = selectedOrderIds.has(o.id);
                    const isCurrentOrder = order && o.id === order.id;
                    
                    return (
                      <button
                        key={o.id}
                        onClick={() => toggleOrderSelection(o.id)}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all active:scale-[0.98] ${
                          isSelected
                            ? 'bg-blue-500/15 border-blue-500 shadow-md'
                            : 'bg-surface-2 dark:bg-surface-2/50 border-line-strong hover:border-line-strong'
                        } ${isCurrentOrder ? 'ring-2 ring-red-400' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'bg-blue-500 text-white' : 'bg-surface-3 text-t3'
                          }`}>
                            {isSelected ? <Check className="h-5 w-5" /> : <MapPin className="h-5 w-5" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-lg text-t1 truncate">{getCustomerName(o)}</span>
                              {isCurrentOrder && (
                                <span className="bg-red-500/20 text-red-700 dark:text-red-300 text-xs px-2 py-1 rounded-full">{t('orders:dispatch.currentBadge')}</span>
                              )}
                            </div>
                            <p className="text-t3 text-sm truncate mt-1">
                              <MapPin className="h-3 w-3 inline mr-1" />
                              {getAddress(o)}
                            </p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xl font-bold text-t1">
                                {formatMoney((typeof o.total_amount === 'number' ? o.total_amount : parseFloat(o.total_amount || '0')))}
                              </span>
                              <span className="text-sm text-t4">
                                <Clock className="h-3 w-3 inline mr-1" />
                                {formatTime(o.order_timestamp)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer - Step 2 (Orders) */}
            <div className="bg-surface-0 border-t border-line px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <button
                    onClick={() => setStep('select-method')}
                    className="px-6 py-3 text-lg font-medium border-2 border-line-strong text-t2 rounded-xl hover:bg-surface-2 transition-colors"
                  >
                    {t('orders:dispatch.back')}
                  </button>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{selectedOrderIds.size}</p>
                    <p className="text-sm text-t3">{t('orders:dispatch.selectedCount')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">{formatMoney(selectedTotal)}</p>
                    <p className="text-sm text-t3">{t('orders:dispatch.total')}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="px-8 py-4 text-lg font-medium border-2 border-line-strong text-t2 rounded-xl hover:bg-surface-2 transition-colors"
                  >
                    {t('common:cancel')}
                  </button>
                  <button
                    onClick={() => setStep('select-driver')}
                    disabled={selectedOrderIds.size === 0}
                    className="cashier-primary px-8 py-4 text-lg font-bold bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-surface-3 disabled:text-t3 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {t('orders:dispatch.continue')}
                    <Truck className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Driver Selection Step */}
            <div className="flex-1 p-8 overflow-y-auto">
              <div className="max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold text-t1 mb-2">{t('orders:dispatch.selectDriverTitle')}</h3>
                <p className="text-t3 mb-8">
                  {t('orders:dispatch.dispatchSubtitle', { count: selectedOrderIds.size, total: selectedTotal.toFixed(2) })}
                </p>

                {drivers.length === 0 ? (
                  <div className="text-center py-12 bg-surface-2 dark:bg-surface-2/50 rounded-xl border border-line-strong">
                    <User className="h-16 w-16 mx-auto text-t4 mb-4" />
                    <p className="text-xl text-t2">{t('orders:dispatch.noDrivers')}</p>
                    <p className="text-t3 mt-2">{t('orders:dispatch.noDriversHint')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {drivers.map((driver) => (
                      <button
                        key={driver.id}
                        onClick={() => setSelectedDriverId(driver.id)}
                        className={`p-6 rounded-2xl border-2 transition-all active:scale-[0.98] ${
                          selectedDriverId === driver.id
                            ? 'bg-blue-500/15 border-blue-500 shadow-lg ring-2 ring-blue-500/30'
                            : 'bg-surface-2 dark:bg-surface-2/50 border-line-strong hover:border-line-strong hover:shadow-md'
                        }`}
                      >
                        <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl font-bold ${
                          selectedDriverId === driver.id
                            ? 'bg-blue-500 text-white'
                            : 'bg-surface-3 text-t2'
                        }`}>
                          {driver.first_name.charAt(0)}{driver.last_name.charAt(0)}
                        </div>
                        <p className="text-xl font-bold text-center text-t1">
                          {driver.first_name} {driver.last_name}
                        </p>
                        {selectedDriverId === driver.id && (
                          <div className="mt-3 flex justify-center">
                            <Check className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* POS terminal picker — only when there's a card order (preload always on) */}
                {showTerminalPicker && (
                  <div className="mt-8 p-5 rounded-2xl border-2 border-line bg-surface-0/40">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-bold text-t1">
                          {t('orders:dispatch.posTerminalTitle', { defaultValue: 'Τερματικό POS (προφόρτωση)' })}
                        </h4>
                        <p className="text-sm text-t3 mt-1">
                          {t('orders:dispatch.posTerminalHint', { defaultValue: 'Η συναλλαγή θα προφορτωθεί στο επιλεγμένο τερματικό — ο πελάτης την ολοκληρώνει με χτύπημα κάρτας. Αν μείνει κενό, δεν γίνεται preload.' })}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <button
                        onClick={() => setSelectedPosTerminalId(null)}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          selectedPosTerminalId === null
                            ? 'bg-surface-2 border-slate-400'
                            : 'bg-surface-1/50 border-line hover:border-line-strong'
                        }`}
                      >
                        <p className="font-semibold text-t2">
                          {t('orders:dispatch.posTerminalNone', { defaultValue: 'Χωρίς preload' })}
                        </p>
                        <p className="text-xs text-t3 mt-1">
                          {t('orders:dispatch.posTerminalNoneHint', { defaultValue: 'Η πληρωμή γίνεται χειροκίνητα όταν επιστρέψει ο διανομέας' })}
                        </p>
                      </button>
                      {posTerminals.map(term => (
                        <button
                          key={term.id}
                          onClick={() => setSelectedPosTerminalId(term.id)}
                          className={`p-4 rounded-xl border-2 transition-all text-left ${
                            selectedPosTerminalId === term.id
                              ? 'bg-emerald-500/15 border-emerald-500 ring-2 ring-emerald-500/30'
                              : 'bg-surface-1/50 border-line hover:border-line-strong'
                          }`}
                        >
                          <p className="font-semibold text-t1">{term.terminal_name}</p>
                          <p className="text-xs text-t3 mt-1">
                            {term.provider_name ? `${term.provider_name} · ` : ''}TID {term.terminal_id}
                          </p>
                          {selectedPosTerminalId === term.id && (
                            <div className="mt-2 flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-sm">
                              <Check className="h-4 w-4" />
                              {t('orders:dispatch.posTerminalSelected', { defaultValue: 'Επιλεγμένο' })}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer - Step 3 */}
            <div className="bg-surface-0 border-t border-line px-6 py-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setStep('select-orders')}
                  className="px-8 py-4 text-lg font-medium border-2 border-line-strong text-t2 rounded-xl hover:bg-surface-2 transition-colors"
                >
                  {t('orders:dispatch.back')}
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="px-8 py-4 text-lg font-medium border-2 border-line-strong text-t2 rounded-xl hover:bg-surface-2 transition-colors"
                  >
                    {t('common:cancel')}
                  </button>
                  <button
                    onClick={handleDispatch}
                    disabled={loading || !selectedDriverId}
                    className="px-10 py-4 text-xl font-bold bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:bg-surface-3 disabled:text-t3 disabled:cursor-not-allowed transition-colors flex items-center gap-3"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin h-6 w-6 border-3 border-white border-t-transparent rounded-full" />
                        {t('orders:dispatch.dispatching')}
                      </>
                    ) : (
                      <>
                        <Truck className="h-6 w-6" />
                        {t('orders:dispatch.dispatchBtn', { count: selectedOrderIds.size })}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DispatchModal;
